import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, CheckCircle2, RotateCcw, Mail, Info } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Card = ({ children, testid }) => (
  <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-primary/5 to-transparent">
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-lg" data-testid={testid}>
      <div className="text-center mb-6">
        <span className="text-2xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>INSUL NUTRIENTS</span>
      </div>
      <div className="bg-card text-card-foreground rounded-2xl border border-border/50 shadow-lg p-8">{children}</div>
    </motion.div>
  </div>
);

export default function RestoreAccountPage() {
  const [params] = useSearchParams();
  const status = params.get('status') || 'invalid_link';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [restored, setRestored] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState(null);

  const errText = (e, fallback) => {
    const d = e.response?.data?.detail;
    return typeof d === 'string' ? d : fallback;
  };

  const handleRestore = async () => {
    if (!email || !password) { toast.error('Enter your email and password'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/restore-account`, { email, password });
      setRestored(true);
    } catch (e) {
      toast.error(errText(e, 'Unable to restore this account'));
    } finally { setBusy(false); }
  };

  const handleResend = async () => {
    if (!email || !password) { toast.error('Enter your email and password'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/resend-cancellation-link`, { email, password });
      if (res.data.email_sent) {
        setLinkSent(true);
      } else if (res.data.cancel_url) {
        setFallbackUrl(res.data.cancel_url);
      }
    } catch (e) {
      toast.error(errText(e, 'Unable to send a new link'));
    } finally { setBusy(false); }
  };

  if (status === 'restored' || restored) {
    return (
      <Card testid="restore-success-card">
        <div className="text-center">
          <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Deletion cancelled</h1>
          <p className="text-muted-foreground mb-6">Your account has been restored. Nothing has been erased, and you can sign in again right away.</p>
          <Button asChild className="rounded-full px-8 py-6 text-lg font-bold bg-gradient-to-r from-primary to-[#8FD14F]" data-testid="restore-success-login-button">
            <Link to="/login">Log in to my account</Link>
          </Button>
        </div>
      </Card>
    );
  }

  if (status === 'nothing_pending') {
    return (
      <Card testid="restore-nothing-card">
        <div className="text-center">
          <Info className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Nothing to cancel</h1>
          <p className="text-muted-foreground mb-6">
            This account has no pending deletion. Either it was already restored — try logging in — or the
            30-day grace period ended and the data has been permanently erased, in which case you're welcome
            to create a new account.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="rounded-full font-bold" data-testid="nothing-pending-login-button"><Link to="/login">Try logging in</Link></Button>
            <Button asChild variant="outline" className="rounded-full"><Link to="/signup">Create a new account</Link></Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card testid="restore-invalid-link-card">
      <div className="flex items-start gap-3 mb-5">
        <AlertTriangle className="w-8 h-8 text-amber-500 flex-shrink-0" />
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>That cancellation link didn't work</h1>
          <p className="text-sm text-muted-foreground mt-1">
            The link was invalid — email programs sometimes break long links. <strong className="text-foreground">Your account
            is still scheduled for permanent deletion</strong> until you cancel, so use one of the options below.
          </p>
        </div>
      </div>

      {linkSent ? (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-4 mb-5" data-testid="resend-success-note">
          <p className="text-sm font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-primary" />A fresh cancellation link is on its way to {email}.</p>
          <p className="text-xs text-muted-foreground mt-1">You can also just restore your account right now with the button below.</p>
        </div>
      ) : null}
      {fallbackUrl ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-5" data-testid="resend-fallback-note">
          <p className="text-sm font-semibold text-amber-800 mb-2">We couldn't send the email — here is your cancellation link. Save it:</p>
          <div className="bg-white border border-border rounded-lg p-2 text-xs break-all select-all">{fallbackUrl}</div>
        </div>
      ) : null}

      <div className="space-y-3 mb-5">
        <div className="space-y-1">
          <Label htmlFor="restore-email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email</Label>
          <Input id="restore-email" data-testid="restore-email-input" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="restore-password" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Password</Label>
          <Input id="restore-password" data-testid="restore-password-input" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl" />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Button onClick={handleRestore} disabled={busy} className="rounded-full py-6 text-base font-bold bg-gradient-to-r from-primary to-[#8FD14F]" data-testid="restore-now-button">
          <RotateCcw className="w-4 h-4 mr-2" />
          {busy ? 'Working...' : 'Restore my account now'}
        </Button>
        <Button onClick={handleResend} disabled={busy} variant="outline" className="rounded-full py-6 text-base font-semibold" data-testid="resend-link-button">
          <Mail className="w-4 h-4 mr-2" />
          Email me a fresh cancellation link
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mt-5 text-center">
        You can also <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="restore-login-link">go to the login page</Link> and
        sign in with your usual details — you'll see a "Restore My Account" option there.
      </p>
    </Card>
  );
}
